import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Plus, Pencil, Trash2, Code } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';
const PortfolioManagement = () => {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [mainImageFile, setMainImageFile] = useState(null);
    const [mainImagePreview, setMainImagePreview] = useState('');
    const [galleryFiles, setGalleryFiles] = useState([]);
    const [galleryPreviews, setGalleryPreviews] = useState([]);
    const [viewMode, setViewMode] = useState('visual');
    const [formData, setFormData] = useState({
        title: '',
        category: '',
        categoryId: '',
        description: '',
        image: '',
        technologies: '',
        url: '',
        order: 0,
        isActive: true,
    });
    const { toast } = useToast();
    useEffect(() => {
        fetchItems();
        fetchCategories();
    }, []);
    const fetchCategories = async () => {
        try {
            const response = await fetch(`${API_URL}/categories?type=portfolio`);
            const data = await response.json();
            setCategories(data);
        }
        catch (error) {
            console.error('Failed to fetch categories');
        }
    };
    const fetchItems = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${API_URL}/portfolio/all`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await response.json();
            setItems(data);
        }
        catch (error) {
            toast({ title: 'Error', description: 'Failed to fetch portfolio items', variant: 'destructive' });
        }
    };
    const handleMainImageChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setMainImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setMainImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };
    const handleGalleryChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) {
            setGalleryFiles(prev => [...prev, ...files]);
            files.forEach(file => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setGalleryPreviews(prev => [...prev, reader.result]);
                };
                reader.readAsDataURL(file);
            });
        }
    };
    const removeGalleryImage = (index) => {
        setGalleryFiles(prev => prev.filter((_, i) => i !== index));
        setGalleryPreviews(prev => prev.filter((_, i) => i !== index));
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('title', formData.title);
            formDataToSend.append('category', formData.category);
            formDataToSend.append('description', formData.description);
            formDataToSend.append('technologies', formData.technologies);
            formDataToSend.append('url', formData.url);
            formDataToSend.append('order', formData.order.toString());
            formDataToSend.append('isActive', formData.isActive.toString());
            if (formData.categoryId) {
                formDataToSend.append('categoryId', formData.categoryId);
            }
            // Main image (single)
            if (mainImageFile) {
                formDataToSend.append('image', mainImageFile);
            }
            // Gallery images (multiple, separate field)
            galleryFiles.forEach((file) => {
                formDataToSend.append('gallery', file);
            });
            const url = editingItem ? `${API_URL}/portfolio/${editingItem.id}` : `${API_URL}/portfolio`;
            const method = editingItem ? 'PUT' : 'POST';
            const response = await fetch(url, {
                method,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formDataToSend,
            });
            if (!response.ok)
                throw new Error('Failed to save item');
            toast({ title: 'Success', description: `Portfolio item ${editingItem ? 'updated' : 'created'} successfully` });
            setIsOpen(false);
            resetForm();
            fetchItems();
        }
        catch (error) {
            toast({ title: 'Error', description: 'Failed to save portfolio item', variant: 'destructive' });
        }
    };
    const handleDelete = async (id) => {
        if (!confirm('Are you sure you want to delete this portfolio item?'))
            return;
        const token = localStorage.getItem('token');
        try {
            await fetch(`${API_URL}/portfolio/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            toast({ title: 'Success', description: 'Portfolio item deleted successfully' });
            fetchItems();
        }
        catch (error) {
            toast({ title: 'Error', description: 'Failed to delete portfolio item', variant: 'destructive' });
        }
    };
    const handleEdit = (item) => {
        setEditingItem(item);
        setFormData({
            title: item.title,
            category: item.category || '',
            categoryId: item.categoryId || '',
            description: item.description || '',
            image: item.image,
            technologies: item.technologies || '',
            url: item.url || '',
            order: item.order,
            isActive: item.isActive,
        });
        // Handle both external URLs and local paths for image preview
        const previewUrl = item.image
            ? (item.image.startsWith('http') ? item.image : `https://qbcron.com${item.image}`)
            : '';
        setMainImagePreview(previewUrl);
        setMainImageFile(null);
        // Load gallery images if they exist
        if (item.images) {
            try {
                const galleryUrls = JSON.parse(item.images);
                setGalleryPreviews(galleryUrls.map((url) => url.startsWith('http') ? url : `https://qbcron.com${url}`));
            }
            catch (e) {
                setGalleryPreviews([]);
            }
        }
        else {
            setGalleryPreviews([]);
        }
        setGalleryFiles([]);
        setIsOpen(true);
    };
    const resetForm = () => {
        setEditingItem(null);
        setFormData({ title: '', category: '', categoryId: '', description: '', image: '', technologies: '', url: '', order: 0, isActive: true });
        setMainImageFile(null);
        setMainImagePreview('');
        setGalleryFiles([]);
        setGalleryPreviews([]);
    };
    return (_jsxs("div", { children: [_jsxs("div", { className: "flex justify-between items-center mb-8", children: [_jsx("h1", { className: "text-3xl font-bold", children: "Portfolio Management" }), _jsxs(Dialog, { open: isOpen, onOpenChange: (open) => { setIsOpen(open); if (!open)
                            resetForm(); }, children: [_jsx(DialogTrigger, { asChild: true, children: _jsxs(Button, { children: [_jsx(Plus, { className: "mr-2 h-4 w-4" }), "Add Portfolio Item"] }) }), _jsxs(DialogContent, { className: "max-w-2xl max-h-[90vh] overflow-y-auto", children: [_jsxs(DialogHeader, { children: [_jsx(DialogTitle, { children: editingItem ? 'Edit Portfolio Item' : 'Add New Portfolio Item' }), _jsx(DialogDescription, { children: "Showcase your best work" })] }), _jsxs("div", { className: "flex gap-2 mb-4 border-b", children: [_jsxs(Button, { type: "button", size: "sm", variant: viewMode === 'visual' ? 'default' : 'ghost', onClick: () => setViewMode('visual'), className: "rounded-b-none", children: [_jsx(Pencil, { className: "h-4 w-4 mr-1" }), "Visual"] }), _jsxs(Button, { type: "button", size: "sm", variant: viewMode === 'source' ? 'default' : 'ghost', onClick: () => setViewMode('source'), className: "rounded-b-none", children: [_jsx(Code, { className: "h-4 w-4 mr-1" }), "Source"] })] }), viewMode === 'source' && (_jsxs("div", { className: "mb-4", children: [_jsx(Label, { children: "HTML Source (Description Field)" }), _jsx(Textarea, { value: formData.description, onChange: (e) => setFormData({ ...formData, description: e.target.value }), className: "font-mono text-sm min-h-[400px] bg-slate-950 text-green-400", placeholder: "Paste your HTML here..." })] })), _jsxs("form", { onSubmit: handleSubmit, className: "space-y-4", children: [_jsxs("div", { children: [_jsx(Label, { htmlFor: "title", children: "Title" }), _jsx(Input, { id: "title", value: formData.title, onChange: (e) => setFormData({ ...formData, title: e.target.value }), required: true })] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "category", children: "Category (Text)" }), _jsx(Input, { id: "category", value: formData.category, onChange: (e) => setFormData({ ...formData, category: e.target.value }), placeholder: "Optional legacy category field" })] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "categorySelect", children: "Category" }), _jsxs(Select, { value: formData.categoryId || undefined, onValueChange: (value) => setFormData({ ...formData, categoryId: value === 'none' ? '' : value }), children: [_jsx(SelectTrigger, { id: "categorySelect", children: _jsx(SelectValue, { placeholder: "Select category (optional)" }) }), _jsxs(SelectContent, { children: [_jsx(SelectItem, { value: "none", children: "No Category" }), categories.map((category) => (_jsx(SelectItem, { value: category.id, children: category.name }, category.id)))] })] })] }), viewMode === 'visual' && (_jsxs("div", { children: [_jsx(Label, { htmlFor: "description", children: "Description" }), _jsx(ReactQuill, { theme: "snow", value: formData.description, onChange: (value) => setFormData({ ...formData, description: value }), modules: {
                                                            toolbar: [
                                                                [{ 'header': [1, 2, 3, false] }],
                                                                ['bold', 'italic', 'underline'],
                                                                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                                                [{ 'color': [] }, { 'background': [] }],
                                                                ['link'],
                                                                ['clean']
                                                            ]
                                                        }, className: "bg-background", style: { height: '200px', marginBottom: '50px' } })] })), _jsxs("div", { children: [_jsx(Label, { htmlFor: "url", className: "text-base font-semibold", children: "Project URL" }), _jsx(Input, { id: "url", type: "url", placeholder: "https://example.com", value: formData.url, onChange: (e) => setFormData({ ...formData, url: e.target.value }), className: "text-base" })] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "mainImage", children: "Main Project Image" }), _jsx(Input, { id: "mainImage", type: "file", accept: "image/*", onChange: handleMainImageChange, className: "cursor-pointer" }), mainImagePreview && (_jsx("div", { className: "mt-3", children: _jsx("img", { src: mainImagePreview, alt: "Main preview", className: "w-full max-h-48 object-cover rounded border" }) }))] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "gallery", children: "Gallery Images (Multiple)" }), _jsx("p", { className: "text-xs text-muted-foreground mb-2", children: "These images will appear at the bottom of the project description" }), _jsx(Input, { id: "gallery", type: "file", accept: "image/*", multiple: true, onChange: handleGalleryChange, className: "cursor-pointer" }), galleryPreviews.length > 0 && (_jsx("div", { className: "mt-3 grid grid-cols-3 gap-2", children: galleryPreviews.map((preview, index) => (_jsxs("div", { className: "relative group", children: [_jsx("img", { src: preview, alt: `Gallery ${index + 1}`, className: "w-full h-24 object-cover rounded border" }), _jsx("button", { type: "button", onClick: () => removeGalleryImage(index), className: "absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity", children: "\u00D7" })] }, index))) }))] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "technologies", children: "Technologies (optional)" }), _jsx(Input, { id: "technologies", value: formData.technologies, onChange: (e) => setFormData({ ...formData, technologies: e.target.value }), placeholder: "React, Node.js, MongoDB" })] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "order", children: "Order" }), _jsx(Input, { id: "order", type: "number", value: formData.order, onChange: (e) => setFormData({ ...formData, order: parseInt(e.target.value) }) })] }), _jsxs("div", { className: "flex items-center space-x-2", children: [_jsx(Switch, { id: "isActive", checked: formData.isActive, onCheckedChange: (checked) => setFormData({ ...formData, isActive: checked }) }), _jsx(Label, { htmlFor: "isActive", children: "Active" })] }), _jsxs(Button, { type: "submit", className: "w-full", children: [editingItem ? 'Update' : 'Create', " Item"] })] })] })] })] }), _jsx("div", { className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6", children: items.map((item) => (_jsxs(Card, { className: "flex flex-col", children: [_jsx(CardHeader, { children: _jsx(CardTitle, { className: "text-xl line-clamp-2", children: item.title }) }), _jsxs(CardContent, { className: "flex-1", children: [item.image && (_jsx("img", { src: item.image.startsWith('http') ? item.image : `https://qbcron.com${item.image}`, alt: item.title, className: "w-full h-48 object-cover rounded mb-3" })), _jsx("div", { className: "text-muted-foreground mb-3 line-clamp-2 text-sm prose prose-sm max-w-none", dangerouslySetInnerHTML: { __html: item.description || '' } }), _jsxs("div", { className: "space-y-1 text-xs text-muted-foreground mb-4", children: [_jsxs("div", { children: ["Category: ", item.category || 'Uncategorized'] }), _jsxs("div", { children: ["Order: ", item.order] }), _jsxs("div", { children: ["Status: ", _jsx("span", { className: item.isActive ? 'text-green-500' : 'text-gray-500', children: item.isActive ? 'Active' : 'Inactive' })] })] }), _jsxs("div", { className: "flex gap-2 mt-auto", children: [_jsxs(Button, { size: "sm", variant: "outline", onClick: (e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleEdit(item);
                                            }, className: "flex-1", type: "button", children: [_jsx(Pencil, { className: "h-4 w-4 mr-1" }), " Edit"] }), _jsx(Button, { size: "sm", variant: "destructive", onClick: (e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleDelete(item.id);
                                            }, type: "button", children: _jsx(Trash2, { className: "h-4 w-4" }) })] })] })] }, item.id))) })] }));
};
export default PortfolioManagement;
